import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import { NgxPermissionsService } from 'ngx-permissions';
import { TranslateModule } from '@ngx-translate/core';
import { SwiperOptions } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import { startWith } from 'rxjs';
import {
  ButtonModule,
  DictionaryItem,
  EmptyComponent,
  FiltersForm,
  RouteFiltersService,
  SelectComponent,
  SpinnerService,
  WithAllOptionPipe,
} from '@hglobal/common';
import { environment } from '@pt/environment';
import { MfeUrlSegments } from '@pt/mfe';
import { TransactionDirection } from '@pt/models';
import { DictionariesService, UiStore } from '@pt/services';

import { TransactionsFilters } from './models';
import { TRANSACTIONS_API, TransactionsApiMockService, TransactionsApiService } from './api';
import { TransactionsStore } from './transactions.store';
import {
  TransactionComponent, TransactionDonutTotalComponent, TransactionMetricTotalComponent,
  TransactionTextTotalComponent,
} from './components';

@Component({
  selector: 'pt-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatPaginator,
    SwiperModule,
    WithAllOptionPipe,
    ButtonModule,
    SelectComponent,
    TransactionComponent,
    EmptyComponent,
    TransactionDonutTotalComponent,
    TransactionMetricTotalComponent,
    TransactionTextTotalComponent,
  ],
  providers: [
    {
      provide: TRANSACTIONS_API,
      useClass: environment.useMocks ? TransactionsApiMockService : TransactionsApiService,
    },
    TransactionsStore,
    RouteFiltersService,
  ],
})
export class TransactionsComponent implements OnInit {
  userId = input<number>();
  isAdmin = input(false, { transform: booleanAttribute });
  forTeam = input(false, { transform: booleanAttribute });
  myTeam = input(false, { transform: booleanAttribute });
  dialog = input(false, { transform: booleanAttribute });

  @HostBinding('class.pt-transactions_dialog')
  private get _dialog() {
    return this.dialog();
  }

  protected store = inject(TransactionsStore);
  readonly uiStore = inject(UiStore);

  readonly #spinnerService = inject(SpinnerService);
  readonly #dictionariesService = inject(DictionariesService);
  readonly #permissionsService = inject(NgxPermissionsService);
  readonly #routeFiltersService = inject(RouteFiltersService<TransactionsFilters>);
  readonly #mfeUrlSegments = inject(MfeUrlSegments);

  readonly #permissions = this.#permissionsService.getPermissions();

  readonly #transactionsReqParams = computed(() => ({
    userId: this.userId(),
    forTeam: this.forTeam(),
    myTeam: this.myTeam(),
    filters: this.store.filters(),
  }));

  readonly transactionsSegment = `/${this.#mfeUrlSegments.transactions}`;
  readonly project = environment.shortName;

  readonly periods = this.#dictionariesService.periods;
  readonly transactionDirections = this.#dictionariesService.transactionDirections;

  readonly metrics = this.store.metrics;
  readonly expenseMetrics = this.store.expenseMetrics;
  readonly incomeMetrics = computed(() => this.store.incomeMetrics()
    .filter(metric => metric.id !== 'funcReward' || !this.isAdmin()
      || !!this.#permissions['AdminTransactionsFunctional']));

  readonly isMetricTotal = computed(() => {
    const charts = this.store.charts();
    return this.store.filters().metric || (charts.length === 1 && charts[0].metrics.length === 1);
  });

  readonly isLoading = toSignal(this.#spinnerService.isSpinning$.pipe(startWith(true)));

  readonly showMore = signal(false);

  readonly swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    grabCursor: true,
    navigation: {
      prevEl: '.pt-transactions__swiper-control-prev',
      nextEl: '.pt-transactions__swiper-control-next',
    },
  };

  filtersForm: FormGroup<FiltersForm<TransactionsFilters>>;

  readonly #isTabletEffect = effect(() => this.showMore.set(this.uiStore.isTabletAndMore()));

  ngOnInit(): void {
    this.store.getMetrics({ forTeam: this.forTeam() });
    this.store.getTransactions(this.#transactionsReqParams);

    this.filtersForm = this.#routeFiltersService.initialize({
      filtersState: this.store.filters,
      updateFilters: filters => this.store.updateFilters(filters),
      ...(this.dialog() ? {} : { queryParamTypes: [] }),
    });
  }

  onPageChange(event: PageEvent): void {
    this.#routeFiltersService.updateMatPageControls(event);
  }

  changeDirection(direction: DictionaryItem<TransactionDirection>): void {
    this.filtersForm.patchValue({ direction: direction.id, metric: null });
  }

  changeMetric(metric: DictionaryItem<string>): void {
    this.filtersForm.patchValue({ direction: null, metric: metric.id });
  }

  toggleShowMore(): void {
    this.showMore.update(value => !value);
  }
}
